import { RightOutlined } from "@ant-design/icons";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import type { MenuDataItem } from "@ant-design/pro-components";
import type { RequestConfig, RunTimeLayoutConfig } from "@umijs/max";
import { history } from "@umijs/max";
import { App, Avatar, Tag } from "antd";
import type { ReactNode } from "react";
import { AntdStaticBridge } from "@/utils/staticAntd";
import FloatingFeedback from "@/components/FloatingFeedback";
import NpsSurvey from "@/components/NpsSurvey";
import { getCurrentUser } from "@/services/stockware/auth";
import { getMySubscription } from "@/services/stockware/subscription";
import { getBootstrapData } from "@/services/stockware/bootstrap";
import { SelectLang } from "@/components";
import logoUrl from "@/assets/logo.svg";
import NoticeIcon from "@/components/NoticeIcon";
import TeamSwitcher from "@/components/TeamSwitcher";
import defaultSettings from "../config/defaultSettings";
import {
  errorConfig,
  goServerError,
  isGatewayError,
} from "./requestErrorConfig";
import { renderLucideMenuIcon } from "@/utils/lucide-menu-icon";

const loginPath = "/user/login";
const accountClosingPath = "/account-closing";
// 未ログインでも開ける公開ページ。ここに入れないと getInitialState / onPageChange が
// ログインへ弾く。/support・/privacy は App Store に提出する URL＝審査担当者が
// 未ログインのまま開くので必須。
const PUBLIC_PATHS = [loginPath, "/support", "/terms", "/privacy"];

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  subscription?: API.Subscription;
  menuTree?: API.RouteItemVO[];
  grantedPermissions?: string[];
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  fetchSubscription?: () => Promise<API.Subscription | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const data = await getCurrentUser({ skipErrorHandler: true });
      // 后端返回 HTTP 200 + { code: 401 } 时，skipErrorHandler 会跳过 errorThrower，
      // 导致返回原始对象而非 unwrap 后的 user。需要校验 loginId 确认是有效用户。
      if (data && (data as any).loginId) {
        return data as API.CurrentUser;
      }
    } catch (error) {
      // getCurrentUser 带 skipErrorHandler，错误不进 errorHandler，需在此自行判定：
      // 后端网关不可达（502/503/504 或无响应）→ 整页 502，而不是误跳登录。
      if (isGatewayError(error)) {
        goServerError();
        return undefined;
      }
      // 其余（含后端 401）按下方逻辑处理
    }
    history.push(loginPath);
    return undefined;
  };

  const fetchSubscription = async () => {
    try {
      return await getMySubscription();
    } catch {
      return undefined;
    }
  };

  const fetchBootstrap = async (): Promise<{
    menuTree?: API.RouteItemVO[];
    grantedPermissions?: string[];
  }> => {
    try {
      const data = await getBootstrapData();
      return {
        menuTree: data?.menuTree,
        grantedPermissions: data?.grantedPermissions,
      };
    } catch {
      return {};
    }
  };

  const { location } = history;
  if (![...PUBLIC_PATHS, accountClosingPath].includes(location.pathname)) {
    const currentUser = await fetchUserInfo();

    // 账户注销中 → 强制跳转到注销恢复页面
    if (currentUser?.accountClosing) {
      history.push(accountClosingPath);
      return {
        fetchUserInfo,
        fetchSubscription,
        currentUser,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }

    const [subscription, bootstrap] = currentUser
      ? await Promise.all([fetchSubscription(), fetchBootstrap()])
      : [undefined, {}];
    return {
      fetchUserInfo,
      fetchSubscription,
      currentUser,
      subscription,
      menuTree: bootstrap?.menuTree,
      grantedPermissions: bootstrap?.grantedPermissions,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    fetchSubscription,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// 后端 platform_sys_menu 仍是旧路径（/product、/stock 等），
// 前端目录已重组到 pages/wms/* 且 URL 带 /wms 前缀（见 routes.ts）。
// 这里把后端旧路径映射到新 /wms/* 路径，菜单点击直达正确路由且高亮正常。
// TODO(backend-migration): platform_sys_menu 迁移到 /wms/* 后删除本映射 + access.ts 的兜底。
const LEGACY_WMS_PATHS = new Set([
  "/product",
  "/stock",
  "/transaction",
  "/inventory-check",
  "/contact",
]);
function mapLegacyPath(path?: string): string | undefined {
  if (path && LEGACY_WMS_PATHS.has(path)) return `/wms${path}`;
  return path;
}

/** 将后端 RouteItemVO 转换为 Ant Design Pro MenuDataItem */
function convertToMenuData(items: API.RouteItemVO[]): MenuDataItem[] {
  return items.map((item) => ({
    path: mapLegacyPath(item.path),
    name: item.meta?.title,
    locale: false,
    icon: item.meta?.icon ? renderLucideMenuIcon(item.meta.icon) : undefined,
    children: item.children ? convertToMenuData(item.children) : undefined,
  }));
}

export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    // 顶栏 logo 用 bundler 引入的带 hash 资源（覆盖 defaultSettings.logo，避免 public 固定名缓存问题）
    logo: logoUrl,
    title: "",
    // logo + 标题左对齐：「在庫管理」用 Google Font 日文标题字体 Zen Kaku Gothic New
    headerTitleRender: (logo) => (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        {logo}
        <span
          style={{
            fontFamily: "'Zen Kaku Gothic New', 'Noto Sans JP', sans-serif",
            fontWeight: 700,
            fontSize: 20,
            color: "rgba(0, 0, 0, 0.88)",
            letterSpacing: "0.04em",
            lineHeight: 1,
          }}
        >
          在庫管理
        </span>
      </div>
    ),
    actionsRender: () => [
      <TeamSwitcher key="TeamSwitcher" />,
      <NoticeIcon key="NoticeIcon" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: undefined,
    footerRender: () =>
      initialState?.currentUser ? (
        <>
          <FloatingFeedback />
          <NpsSurvey />
        </>
      ) : null,
    menuFooterRender: (props) => {
      if (!initialState?.currentUser) return null;
      const displayName =
        initialState.currentUser.name ||
        initialState.currentUser.loginId ||
        "?";
      const firstLetter = displayName.charAt(0).toUpperCase();
      const avatarNode = initialState.currentUser.avatar ? (
        <Avatar size="small" src={initialState.currentUser.avatar} />
      ) : (
        <Avatar
          size="small"
          style={{ backgroundColor: "#1677ff", fontSize: 14 }}
        >
          {firstLetter}
        </Avatar>
      );
      const planType = initialState.subscription?.planType ?? "FREE";
      const planLabel =
        planType === "PRO" ? "Pro" : planType === "TRIAL" ? "Trial" : "Free";
      const planColor =
        planType === "PRO"
          ? "green"
          : planType === "TRIAL"
            ? "orange"
            : "default";
      return (
        <div
          onClick={() => history.push("/settings")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            cursor: "pointer",
            justifyContent: props?.collapsed ? "center" : "flex-start",
            borderRadius: 6,
            minWidth: 0,
          }}
        >
          {avatarNode}
          {!props?.collapsed && (
            <>
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 14,
                }}
              >
                {displayName}
              </span>
              <Tag
                color={planColor}
                style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: "16px" }}
              >
                {planLabel}
              </Tag>
              <RightOutlined style={{ color: "#999", fontSize: 12 }} />
            </>
          )}
        </div>
      );
    },
    onPageChange: () => {
      const { location } = history;
      // /502 是网关不可达整页（layout:false），不要因无 currentUser 把它弹去登录
      // PUBLIC_PATHS（/support 等）同理：未登录也必须能打开
      if (
        !initialState?.currentUser &&
        !PUBLIC_PATHS.includes(location.pathname) &&
        location.pathname !== "/502"
      ) {
        history.push(loginPath);
      }
      // 账户注销中 → 强制跳转到注销恢复页面
      if (
        initialState?.currentUser?.accountClosing &&
        location.pathname !== accountClosingPath
      ) {
        history.push(accountClosingPath);
      }
    },
    menuHeaderRender: undefined,
    menuDataRender: (menuData: MenuDataItem[]) => {
      // 使用后端返回的动态菜单，不降级到 routes.ts 静态配置
      // 避免未授权用户看到全部菜单
      if (initialState?.menuTree && initialState.menuTree.length > 0) {
        return convertToMenuData(initialState.menuTree);
      }
      // Bootstrap 未返回菜单时，只保留基础路由（dashboard、settings）
      return menuData.filter(
        (item) => item.path === "/dashboard" || item.path === "/settings"
      );
    },
    ...initialState?.settings,
  };
};

// 在 umi 的 <App> 外再包一层（component="div"，因主题开了 cssVar 需挂在真实 DOM 上），
// 把带 ConfigProvider 上下文的 message/modal/notification 实例注入 staticAntd，
// 供非组件代码（requestErrorConfig）与各页面统一调用，消除 antd v6 静态方法告警。
export function innerProvider(container: ReactNode) {
  return (
    <App component="div" style={{ height: "100%" }}>
      <AntdStaticBridge />
      {container}
    </App>
  );
}

export const request: RequestConfig = {
  // baseURL 由 config/config.prod.ts 通过 define 注入；dev 留空走 UMI proxy
  baseURL: process.env.API_BASE_URL || "",
  ...errorConfig,
};

const SUPPORTED_LOCALES = ["ja-JP", "zh-CN", "zh-TW", "en-US"] as const;

function detectBrowserLocale(): string {
  if (typeof navigator === "undefined") return "ja-JP";
  const candidates = [
    navigator.language,
    ...((navigator.languages as readonly string[] | undefined) ?? []),
  ].filter(Boolean);
  for (const raw of candidates) {
    const tag = String(raw).toLowerCase();
    const exact = SUPPORTED_LOCALES.find((s) => s.toLowerCase() === tag);
    if (exact) return exact;
    if (
      tag.startsWith("zh-tw") ||
      tag.startsWith("zh-hk") ||
      tag.startsWith("zh-mo") ||
      tag.startsWith("zh-hant")
    ) {
      return "zh-TW";
    }
    if (tag.startsWith("zh")) return "zh-CN";
    if (tag.startsWith("ja")) return "ja-JP";
    if (tag.startsWith("en")) return "en-US";
  }
  return "ja-JP";
}

export const locale = {
  getLocale() {
    if (typeof window !== "undefined" && window.localStorage) {
      const stored = window.localStorage.getItem("umi_locale");
      if (stored) return stored;
    }
    return detectBrowserLocale();
  },
};
